import store from '@/store';
import deparam from 'deparam';
import { cloneDeep, isEqual, isEmpty } from '@/helpers/utilities';
import { EnumType } from 'json-to-graphql-query';
import paramHelper from '@/helpers/param';
import { distanceFieldName } from '../orderByNames';
import {
  registrationYearFilterName,
  registrationDateFilterName,
  genericColourFilterName,
  odometerMilesFilterName,
  odometerKilometersFilterName,
  distanceFilterName,
  conditionFilterName,
  variantFilterName,
  engineSizeFilterName,
  locationHashFilterName,
  franchiseHashFilterName,
} from '../filterNames';

const getPagination = ({ currentPage, pageSize }) => {
  if (!currentPage || !pageSize) return false;

  return {
    currentPage,
    pageSize,
  };
};

const getOfferSearchAttributes = (searchObject) => {
  const object = cloneDeep(searchObject);
  delete object[registrationYearFilterName];
  delete object[registrationDateFilterName];
  delete object[genericColourFilterName];
  delete object[odometerMilesFilterName];
  delete object[odometerKilometersFilterName];
  delete object[distanceFilterName];

  if (searchObject[locationHashFilterName]) {
    delete object[locationHashFilterName];

    const siteFranchiseHash = store?.getters['ndc/app/getSiteFranchiseHash'] || '';
    if (siteFranchiseHash) {
      object[franchiseHashFilterName] = [siteFranchiseHash];
    }
  }

  object[conditionFilterName] = 'New Offer';

  if (!isEmpty(searchObject?.finances)) {
    const { payment } = searchObject.finances;
    object.finances = { payment: !isEmpty(payment) ? { ...payment } : { from: 0 } };
  }

  return {
    or: [object, searchObject],
  };
};

const getSearchAttributes = (searchCriteria) => {
  if (!searchCriteria) return false;
  const searchObject = {};

  Object.keys(searchCriteria).forEach((key) => {
    const value = searchCriteria[key];
    // no value exists exit function
    if (!value) return;

    // value contains to and from fields format as to and from
    if (typeof value === 'object' && 'from' in value && 'to' in value) {
      searchObject[key] = {
        from: value.from,
        to: value.to,
      };

      if (value.items) searchObject[key].items = value.items;
      return;
    }

    // if the distance field with no to value exit function
    if (distanceFieldName === key && value.distance <= 0) return;

    // if distance field with lat and lon defined format for distance
    if (distanceFieldName === key && 'lat' in value && 'lon' in value) {
      searchObject[key] = {
        distance: Number(value.distance),
        unit: new EnumType(value.unit),
        lat: parseFloat(value.lat),
        lon: parseFloat(value.lon),
      };
      return;
    }

    switch (key) {
      // variants are treated differently to other filters
      case variantFilterName:
        searchObject.variant = searchCriteria[key];
        break;
      // Engines array is treated differently to other filters
      case engineSizeFilterName:
        searchObject[key] = value.map((engine) => {
          const mapped = {};
          if (engine.from) mapped.from = engine.from;

          if (engine.to) mapped.toNotInc = engine.to;
          return mapped;
        });
        break;
      default:
        searchObject[key] = value;
    }
  });

  // !! additional logic to query for new offers based on monthly payment
  // only apply when the condition has new offer or is not filtered
  // currently done this way as new offers only return one finance value
  if (!searchObject[conditionFilterName]
    || (searchObject[conditionFilterName] && searchObject[conditionFilterName].indexOf('New Offer') !== -1)) {
    return getOfferSearchAttributes(searchObject);
  }

  return searchObject;
};

const getParameters = (payload) => {
  const {
    criteria,
    currentPage,
    pageSize,
    sortParams,
    financesDataContext,
    newFinancesDataContext,
  } = payload;
  const queryAttributes = {};
  const paginationParams = getPagination({ currentPage, pageSize });
  const searchParams = getSearchAttributes(criteria);

  if (criteria) {
    queryAttributes.searchParams = searchParams;
  }

  if (paginationParams) {
    queryAttributes.pagination = paginationParams;
  }

  if (financesDataContext) {
    queryAttributes.financesDataContext = financesDataContext;
  }

  if (newFinancesDataContext) {
    queryAttributes.conditionalFinancesDataContext = newFinancesDataContext;
  }

  if (sortParams && sortParams.length) {
    queryAttributes.sortParams = sortParams;
  }

  return queryAttributes;
};

function decode(str) {
  try {
    return decodeURIComponent(str);
  } catch (err) {
    return str;
  }
}

const serializeObjToQuery = (searchCriteria) => {
  const res = {};

  const reducer = (obj, parentPrefix = null) => (prev, key) => {
    const val = obj[key];
    const keyEnCode = encodeURIComponent(key);
    const prefix = parentPrefix ? `${parentPrefix}[${keyEnCode}]` : keyEnCode;

    if (val == null || typeof val === 'function') {
      prev.push(`${prefix}=`);
      return prev;
    }

    if (['number', 'boolean', 'string'].includes(typeof val)) {
      prev.push(`${prefix}=${encodeURIComponent(val)}`);
      return prev;
    }

    prev.push(
      Object.keys(val)
        .reduce(reducer(val, prefix), [])
        .join('&'),
    );

    return prev;
  };

  const query = Object.keys(searchCriteria)
    .reduce(reducer(searchCriteria), [])
    .join('&');

  query.split('&').forEach((param) => {
    const parts = param.replace(/\+/g, ' ').split('=');
    const key = decode(parts.shift());
    const val = parts.length > 0 ? decode(parts.join('=')) : null;
    if (!val) return;
    res[key] = val;
  });

  return {
    string: query,
    query: res,
  };
};

const getUrlParamsObject = () => {
  // eslint-disable-next-line no-restricted-globals
  const { search } = location;
  if (!search) return {};
  const string = new URLSearchParams(search).toString();
  if (!string) return {};
  return deparam(string);
};

const changeUrlParamsHelper = ({
  updateSearchParams,
  updateSortParams,
  updatePageParam,
  updateLocation,
  updateVersion,
}) => {
  const search = getUrlParamsObject();
  const { currentPage, sortParams, location, nfcSearchVersion, ...searchParams } = search;
  const updatedSearchParams = updateSearchParams !== undefined ? updateSearchParams : searchParams;
  const updatedSortArray = updateSortParams !== undefined ? updateSortParams : sortParams || [];
  const updatedLocation = updateLocation !== undefined ? updateLocation : location || '';
  const updatedVersion = updateVersion || nfcSearchVersion;
  // eslint-disable-next-line no-nested-ternary
  const updatePage = updatePageParam !== undefined
    ? updatePageParam
    : /^[0-9]$/.test(currentPage)
      ? Number(currentPage)
      : null;

  const rawQueryObject = {
    ...updatedSearchParams,
    sortParams: updatedSortArray,
    currentPage: updatePage,
    location: updatedLocation,
  };

  const { query } = serializeObjToQuery(rawQueryObject);
  if (isEqual(query, search)) return false;

  const newSearchString = isEmpty(query) || !updatedVersion ? '' : paramHelper({ ...query, nfcSearchVersion: updatedVersion });
  let newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
  if (newSearchString) newurl += `?${newSearchString}`;

  window.history.pushState({ path: newurl }, '', newurl);
  return newSearchString;
};

export default {
  getParameters,
  serializeObjToQuery,
  getUrlParamsObject,
  changeUrlParamsHelper,
};
